import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import type { InlineLabelPlacement } from '../forms/layouts/types';
import { IconButtonSize, IconColor } from './types';

export const buttonBase = css`
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 250ms ease-in-out;
  padding: ${theme.spacing(1)};
  gap: ${theme.spacing(1)};
  position: relative;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px white, 0 0 0 4px ${theme.colors.primary50};
  }

  &:focus:not(:active)::before,
  &:focus-within:not(:active)::before {
    opacity: 1;
  }

  &:hover:not([aria-disabled='true']),
  &:focus {
    background: ${theme.colors.neutral5};
    transition: fill 250ms ease-in-out;
  }

  &[aria-disabled='true'] {
    cursor: default;
    color: ${theme.colors.neutral20};
    svg {
      fill: ${theme.colors.neutral20};
    }
  }
`;

const sizeNormal = css`
  min-width: 40px;
  height: 40px;
  font-size: ${theme.fontSize(16)};
`;

const sizeNormalIconOnly = css`
  width: 40px;
`;

const sizeSmall = css`
  min-width: 32px;
  height: 32px;
  font-size: ${theme.fontSize(14)};
`;

const sizeSmallIconOnly = css`
  width: 32px;
`;

const sizeXSmall = css`
  min-width: 24px;
  height: 24px;
  font-size: ${theme.fontSize(14)};
`;

const sizeXSmallIconOnly = css`
  width: 24px;
`;

const sizeXXSmall = css`
  min-width: 16px;
  height: 16px;
  font-size: ${theme.fontSize(14)};
`;

const sizeXXSmallIconOnly = css`
  width: 16px;
`;

const labelPlacementRight = css`
  flex-direction: row;
`;

const labelPlacementLeft = css`
  flex-direction: row-reverse;
`;

const destructiveStyles = css`
  color: ${theme.colors.critical50};
  svg {
    fill: ${theme.colors.critical50};
  }
`;

const nonDestructiveStyles = css`
  color: ${theme.colors.neutral80};
`;

const colorLight = css`
  &:hover {
    color: ${theme.colors.neutral80};
  }
  &:hover:not([aria-disabled='true']),
  &:focus {
    svg {
      fill: ${theme.colors.neutral80};
    }
  }
`;

const showLabelAlwaysStyles = css`
  width: auto;
  border-radius: 4px;
  &::before {
    border-radius: 4px;
  }
`;

const svgFocusStyle = css`
  &:hover:not([aria-disabled='true']),
  &:focus {
    svg {
      &:focus::before {
        width: calc(100% + ${theme.spacing(1)});
        height: calc(100% + ${theme.spacing(1)});
      }
    }
  }
`;

type IconButtonStyleProps = {
  size: IconButtonSize;
  color: IconColor;
  showLabelAlways?: boolean;
  labelPlacement?: InlineLabelPlacement;
  destructive?: boolean;
};

export const getIconButtonClassName = ({
  size,
  color,
  showLabelAlways,
  labelPlacement,
  destructive,
}: IconButtonStyleProps) => {
  const sizeClass = (() => {
    switch (size) {
      case 'normal':
        return cx(sizeNormal, !showLabelAlways && sizeNormalIconOnly);
      case 'small':
        return cx(sizeSmall, !showLabelAlways && sizeSmallIconOnly);
      case 'xsmall':
        return cx(sizeXSmall, !showLabelAlways && sizeXSmallIconOnly);
      case 'xxsmall':
        return cx(sizeXXSmall, !showLabelAlways && sizeXXSmallIconOnly);
    }
  })();

  return cx(
    buttonBase,
    sizeClass,
    labelPlacement === 'right' ? labelPlacementRight : labelPlacementLeft,
    destructive ? destructiveStyles : nonDestructiveStyles,
    color === 'light' && colorLight,
    showLabelAlways && showLabelAlwaysStyles,
    svgFocusStyle
  );
};
